import React, { useState, useRef, useCallback, useEffect } from "react";
import { Spin, Typography } from "antd";
import { LoadingOutlined, ArrowDownOutlined, CheckCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

export interface MobilePullToRefreshListProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void> | void;
  onLoadMore?: () => Promise<void> | void;
  hasMore?: boolean;
  isRefreshing?: boolean;
  isLoadingMore?: boolean;
  pullThreshold?: number; // default 65px
  scrollLoadThreshold?: number; // default 150px before bottom
  totalItems?: number;
  currentItemsCount?: number;
  style?: React.CSSProperties;
}

export const MobilePullToRefreshList: React.FC<MobilePullToRefreshListProps> = ({
  children,
  onRefresh,
  onLoadMore,
  hasMore = false,
  isRefreshing = false,
  isLoadingMore = false,
  pullThreshold = 65,
  scrollLoadThreshold = 150,
  totalItems,
  currentItemsCount,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState<number>(0);
  const [pullState, setPullState] = useState<"idle" | "pulling" | "canRelease" | "refreshing">("idle");
  const touchStartY = useRef<number>(0);
  const isPullingRef = useRef<boolean>(false);

  // Sync internal pullState with prop isRefreshing
  useEffect(() => {
    if (isRefreshing) {
      setPullState("refreshing");
      setPullDistance(pullThreshold);
    } else if (pullState === "refreshing") {
      setPullState("idle");
      setPullDistance(0);
    }
  }, [isRefreshing, pullThreshold]);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;

    if (container.scrollTop <= 0 && !isRefreshing && !isLoadingMore) {
      touchStartY.current = e.touches[0].clientY;
      isPullingRef.current = true;
    } else {
      isPullingRef.current = false;
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isPullingRef.current || isRefreshing) return;
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) {
      isPullingRef.current = false;
      setPullDistance(0);
      setPullState("idle");
      return;
    }

    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;

    if (diff > 0) {
      // Dampen pulling distance visually
      const dampened = Math.min(diff * 0.45, pullThreshold * 1.5);
      setPullDistance(dampened);

      if (dampened >= pullThreshold) {
        setPullState("canRelease");
      } else {
        setPullState("pulling");
      }
    } else {
      setPullDistance(0);
      setPullState("idle");
    }
  };

  const handleTouchEnd = async () => {
    if (!isPullingRef.current) return;
    isPullingRef.current = false;

    if (pullState === "canRelease" || pullDistance >= pullThreshold) {
      setPullState("refreshing");
      setPullDistance(pullThreshold);
      try {
        await onRefresh();
      } finally {
        setPullState("idle");
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
      setPullState("idle");
    }
  };

  // Scroll listener for Load More (infinite scroll when near bottom)
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || !onLoadMore || !hasMore || isLoadingMore || isRefreshing) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    if (distanceFromBottom <= scrollLoadThreshold) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, isLoadingMore, isRefreshing, scrollLoadThreshold]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: "relative",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        height: "100%",
        width: "100%",
        paddingBottom: 24,
        ...style,
      }}
    >
      {/* Pull To Refresh Top Header Indicator */}
      <div
        style={{
          height: pullDistance,
          overflow: "hidden",
          transition: pullState === "refreshing" || pullState === "idle" ? "height 0.25s ease" : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f0f5ff",
          borderRadius: 8,
          marginBottom: pullDistance > 0 ? 8 : 0,
        }}
      >
        <div style={{ textAlign: "center", display: "flex", alignItems: "center", gap: 8 }}>
          {pullState === "pulling" && (
            <>
              <ArrowDownOutlined style={{ color: "#1677ff", fontSize: 16 }} />
              <Text type="secondary" style={{ fontSize: 13 }}>
                下にスワイプして更新...{/* vuốt xuống để làm mới... */}
              </Text>
            </>
          )}
          {pullState === "canRelease" && (
            <>
              <ArrowDownOutlined style={{ color: "#52c41a", transform: "rotate(180deg)", fontSize: 16 }} />
              <Text style={{ color: "#52c41a", fontSize: 13, fontWeight: "bold" }}>
                指を離して更新{/* Thả ra để cập nhật */}
              </Text>
            </>
          )}
          {pullState === "refreshing" && (
            <>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
              <Text style={{ color: "#1677ff", fontSize: 13 }}>
                更新中...{/* Đang làm mới danh sách... */}
              </Text>
            </>
          )}
        </div>
      </div>

      {/* Main List Content */}
      {children}

      {/* Footer Load More Indicator */}
      <div style={{ padding: "16px 0", textAlign: "center" }}>
        {isLoadingMore ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
            <Text type="secondary" style={{ fontSize: 13 }}>
              読み込み中...{/* Đang tải thêm dữ liệu... */}
            </Text>
          </div>
        ) : hasMore ? (
          <Text type="secondary" style={{ fontSize: 12, cursor: "pointer" }} onClick={onLoadMore}>
            下にスクロールしてさらに読み込む（またはここをタップ）{/* Cuộn xuống để tải thêm (hoặc nhấn vào đây) */}
          </Text>
        ) : currentItemsCount && currentItemsCount > 0 ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              全{totalItems || currentItemsCount}件中{currentItemsCount}件を表示しました{/* Đã hiển thị tất cả các mục */}
            </Text>
          </div>
        ) : null}
      </div>
    </div>
  );
};
